// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { Button, Toast } from '@imprivata-cloud/design-system';
import { Spin } from 'antd';
import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import './Applications.less';
import { Entitlement, SearchParams } from '@api/constants';
import { hasEntitlement } from '@api/utils';
import { useSession } from '@common/authorization/context/Session';
import { withRole } from '@common/authorization/Roles';
import { ErrorCode } from '@errorHandler/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import { ROLE_TYPE_ADMIN } from '@portal/constants';
import { omitSearchParam } from '@utils/navigationSearch';
import { featureSupported } from '../../../featureFlagsUtils';
import AppProfiles from './appProfiles/AppProfiles';
import { APP_PROFILES_FEATURE_FLAG } from './appProfiles/AppProfiles.types';
import ApplicationsRow from './ApplicationsRow';
import ApplicationsEmpty from './empty/ApplicationsEmpty';
import { useAppConfigQuery } from './hooks/useAppConfig';
import { useAppIntegrationQuery } from './hooks/useAppIntegration';
import { useOidcAppConfig } from './hooks/useOidcAppConfig';
import { useOidcDiscoveryQuery } from './hooks/useOidcDiscoveryQuery';
import HyperdriveModal, { isAppConfigured } from './modals/HyperdriveModal';

const APP_ID = 'epic-hyperdrive';
const TRANSLATION_ROOT = 'apps';

function Applications() {
  const { t } = useTranslation();
  const { tenantInfo, tenantId } = useSession();
  const { emitError } = useErrorContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);
  const [headerEl, setHeaderEl] = useState<Element | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  useLayoutEffect(() => {
    setHeaderEl(document.querySelector('.settings-header'));
    return () => setHeaderEl(null);
  }, []);

  const entitled = hasEntitlement(tenantInfo, Entitlement.SSO);

  const {
    data: integrationData,
    isLoading: isIntegrationLoading,
    isFetching: isIntegrationFetching,
    error: integrationError,
  } = useAppIntegrationQuery(APP_ID, { enabled: entitled });

  const {
    data: configData,
    isLoading: isAppConfigLoading,
    isFetching: isAppConfigFetching,
    error: configError,
  } = useAppConfigQuery(APP_ID, { enabled: entitled });

  const {
    oidcConfig,
    isLoading: isOidcLoading,
    error: oidcConfigError,
    createOidcConfig,
    resetCreateOidcConfig,
    updateOidcConfig,
    deleteOidcConfig,
  } = useOidcAppConfig(entitled ? tenantId : undefined);

  const { data: discoveryData, error: discoveryError } = useOidcDiscoveryQuery(
    entitled ? tenantId : undefined,
  );

  useEffect(() => {
    if (discoveryError) {
      emitError(new AppError(discoveryError));
    }
  }, [discoveryError, emitError]);

  useEffect(() => {
    if (oidcConfigError) {
      emitError(new AppError(oidcConfigError));
    }
  }, [oidcConfigError, emitError]);

  useEffect(() => {
    if (integrationError) {
      emitError(new AppError(integrationError));
    }
  }, [integrationError, emitError]);

  useEffect(() => {
    // `app-not-configured` means the tenant simply hasn't set up Epic
    // Hyperdrive yet — an expected outcome, not a failure to surface.
    if (configError && configError.code !== ErrorCode.APP_NOT_CONFIGURED) {
      emitError(new AppError(configError));
    }
  }, [configError, emitError]);

  const appIntegration = integrationData?.appIntegration;
  const appConfig = configData?.appConfig;
  const isConfigured = !!appIntegration && isAppConfigured(appIntegration, appConfig);

  // `?edit=epic-hyperdrive` (the Workflows "Edit Epic Hyperdrive" link) opens the modal once both
  // queries settle: Edit when Epic Hyperdrive is set up, Add when it is not. If a query failed,
  // the page shows as usual. The param is then dropped, so a reload
  // or Back does not open the modal again. Settled means fetched, not just loaded: on a return
  // visit the cached data shows while the queries refetch (`staleTime: 0`), and the modal's form
  // takes its values once, so it must not open on the cached ones.
  const editRequested = searchParams.get(SearchParams.EDIT_APP) === APP_ID;
  const settled = !isIntegrationFetching && !isAppConfigFetching;
  const configRead = !configError || configError.code === ErrorCode.APP_NOT_CONFIGURED;
  const canOpen = !!appIntegration && !integrationError && configRead;
  useEffect(() => {
    if (!editRequested || !settled) {
      return;
    }
    if (canOpen) {
      setIsModalOpen(true);
    }
    setSearchParams(params => omitSearchParam(params, SearchParams.EDIT_APP), { replace: true });
  }, [editRequested, settled, canOpen, setSearchParams]);

  if (!entitled) {
    return null;
  }

  const isOidcConfigured = !!oidcConfig;

  const renderContent = () => {
    if (isIntegrationLoading) {
      return (
        <div className="applications-loading">
          <Spin />
          <span>{t(`${TRANSLATION_ROOT}.loading`)}</span>
        </div>
      );
    }
    if (!isConfigured) {
      return <ApplicationsEmpty onAdd={() => setIsModalOpen(true)} />;
    }
    return (
      <ApplicationsRow
        appIntegration={appIntegration}
        appConfig={appConfig}
        isOidcConfigured={isOidcConfigured}
        onEdit={() => setIsModalOpen(true)}
      />
    );
  };

  return (
    <>
      {headerEl &&
        !isConfigured &&
        !isIntegrationLoading &&
        createPortal(
          <Button variant="secondary" onClick={() => setIsModalOpen(true)}>
            {t(`${TRANSLATION_ROOT}.add-cta`)}
          </Button>,
          headerEl,
        )}
      {headerEl &&
        successBanner &&
        createPortal(
          <div className="applications-success-banner">
            <Toast
              status="success"
              title={successBanner}
              onClose={() => setSuccessBanner(null)}
              onOpenChange={open => !open && setSuccessBanner(null)}
            />
          </div>,
          headerEl,
        )}
      <div className="applications-container">
        {renderContent()}
        {isModalOpen && appIntegration && (
          <HyperdriveModal
            appIntegration={appIntegration}
            appConfig={appConfig}
            isAppConfigLoading={isAppConfigLoading}
            redirectUri={configData?.redirectUri}
            oidcConfig={oidcConfig ?? undefined}
            isOidcLoading={isOidcLoading}
            discoveryData={discoveryData}
            createOidcConfig={createOidcConfig}
            resetCreateOidcConfig={resetCreateOidcConfig}
            updateOidcConfig={updateOidcConfig}
            deleteOidcConfig={deleteOidcConfig}
            onClose={() => setIsModalOpen(false)}
            onSaved={() => setSuccessBanner(t(`${TRANSLATION_ROOT}.updated-banner`))}
            onOidcCreated={() => setSuccessBanner(t(`${TRANSLATION_ROOT}.added-banner`))}
          />
        )}
      </div>
      {featureSupported(APP_PROFILES_FEATURE_FLAG) && <AppProfiles />}
    </>
  );
}

export default withRole({ authorizedRoles: [ROLE_TYPE_ADMIN] })(Applications);
